import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { BrandSettingsNav } from "@/components/brand/settings/brand-settings-nav"
import Heading from "@/components/heading"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

/**
 * OKS-21 — Brand Settings shell layout.
 *
 * Mirrors the existing user-settings layout (`layouts/settings/layout.tsx`)
 * shape but scopes to **brand-level** settings (vs the user-scoped
 * profile/password/email/sessions/appearance pages).
 *
 * **One sub-nav item in v1** (Invites). UX Spec calls out the
 * intentional shell: future brand-level settings (notifications,
 * integrations, branding) plug in by adding entries to
 * `<BrandSettingsNav>` — no layout churn, no sidebar churn.
 *
 * Locked testid: `brand-settings-layout`. The page heading copy
 * comes from i18n (`invites.settings.title` / `subtitle` for the
 * Invites sub-page; future pages use their own keys).
 */

interface BrandSettingsLayoutProps {
  /** Section heading, rendered above the sub-nav + content area.
   *  Defaults to the namespaced "Settings" string. */
  title?: string
  /** Optional subtitle. */
  description?: string
  /** OKS-672 — widen the content column. Settings *forms* use the
   *  narrow `max-w-xl` column, but the relocated Team page is a wide
   *  data table + pagination that must not be squeezed. When `true`,
   *  the content column drops the form max-widths and fills the shell.
   *  Defaults to `false` (narrow form column). */
  wide?: boolean
}

export default function BrandSettingsLayout({
  children,
  title,
  description,
  wide = false,
}: PropsWithChildren<BrandSettingsLayoutProps>) {
  const { t } = useTranslation()

  return (
    <div data-testid="brand-settings-layout" className="px-4 py-6">
      <Heading
        title={title ?? t("settings.brand.nav.section")}
        description={description ?? ""}
      />

      <div className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-12">
        <aside className="w-full max-w-xl lg:w-48">
          <BrandSettingsNav />
        </aside>

        <Separator className="my-6 md:hidden" />

        <div className={cn("flex-1", { "md:max-w-2xl": !wide })}>
          <section className={cn("space-y-12", { "max-w-xl": !wide })}>
            {children}
          </section>
        </div>
      </div>
    </div>
  )
}
